import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Switch,
  Typography,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";
import { GET, ADD, UPDATE } from "../Functions/apiFunction";
import api from "../Data/api";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";

function AutoOrderAcceptance() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const admin = JSON.parse(sessionStorage.getItem("admin"));
  const token = `Bearer ${admin.token}`;

  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);
  const [lastAcceptedCount, setLastAcceptedCount] = useState(null);
  const [reFetch, setReFetch] = useState(false);

  // always a valid MUI severity value
  const [snakbarOpen, setSnakbarOpen] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [alertMsg, setAlertMsg] = useState("");

  const showAlert = (type, msg) => {
    setAlertType(type);
    setAlertMsg(msg);
    setSnakbarOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const settingRes = await GET(token, `${api}/get_auto_accept_setting`);
        if (settingRes?.response === 200) {
          setAutoAcceptEnabled(settingRes.data?.value === "1");
        }
      } catch (e) {
        showAlert("error", "Failed to load settings");
      }

      try {
        const ordersRes = await GET(token, `${api}/get_order`);
        if (ordersRes?.response === 200 && Array.isArray(ordersRes.data)) {
          const pending = ordersRes.data.filter(
            (o) => o.status === 0 || o.status === "0"
          );
          setPendingCount(pending.length);
        }
      } catch (e) {
        setPendingCount(0);
      }

      setLoading(false);
    };
    fetchData();
  }, [reFetch, token]);

  const handleToggle = async (e) => {
    const newVal = e.target.checked ? "1" : "0";
    setToggling(true);
    const res = await UPDATE(
      token,
      `${api}/toggle_auto_accept`,
      JSON.stringify({ value: newVal })
    );
    setToggling(false);
    if (res?.response === 200) {
      setAutoAcceptEnabled(e.target.checked);
      showAlert(
        "success",
        `Auto accept ${e.target.checked ? "enabled" : "disabled"} successfully`
      );
    } else {
      showAlert("error", "Failed to update setting");
    }
  };

  const handleManualAccept = async () => {
    setAccepting(true);
    const res = await ADD(
      token,
      `${api}/auto_accept_orders`,
      JSON.stringify({})
    );
    setAccepting(false);
    if (res?.response === 200) {
      setLastAcceptedCount(res.accepted_count ?? 0);
      showAlert(
        "success",
        `${res.accepted_count ?? 0} order(s) accepted successfully`
      );
      setReFetch((p) => !p);
    } else {
      showAlert("error", "Failed to accept orders");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Snackbar
        open={snakbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnakbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnakbarOpen(false)}
          severity={alertType}
          sx={{ width: "100%" }}
        >
          {alertMsg}
        </Alert>
      </Snackbar>

      <Typography variant="h3" component="h3" sx={{ mb: 3 }}>
        Auto Order Acceptance
      </Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ mt: 6 }}>
          <CircularProgress color="secondary" />
        </Stack>
      ) : (
        <Stack spacing={3} sx={{ maxWidth: 600 }}>
          {/* Status Cards */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Paper
              elevation={3}
              sx={{
                flex: 1,
                p: 3,
                borderRadius: 2,
                backgroundColor: colors.primary[400],
                textAlign: "center",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Pending Orders
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  color:
                    pendingCount > 0
                      ? "#f4a261"
                      : colors.greenAccent[400],
                }}
              >
                {pendingCount ?? "—"}
              </Typography>
            </Paper>

            {lastAcceptedCount !== null && (
              <Paper
                elevation={3}
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: colors.primary[400],
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Last Accepted
                </Typography>
                <Typography
                  variant="h2"
                  sx={{ fontWeight: "bold", color: colors.greenAccent[400] }}
                >
                  {lastAcceptedCount}
                </Typography>
              </Paper>
            )}
          </Stack>

          {/* Auto Accept Toggle */}
          <Paper
            elevation={3}
            sx={{ p: 3, borderRadius: 2, backgroundColor: colors.primary[400] }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Automatic Acceptance
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  When enabled, new pending orders are automatically confirmed
                  every minute via server scheduler.
                </Typography>
              </Box>
              <Stack alignItems="center" spacing={0.5}>
                {toggling ? (
                  <CircularProgress size={24} color="secondary" />
                ) : (
                  <Switch
                    checked={autoAcceptEnabled}
                    onChange={handleToggle}
                    color="secondary"
                    size="medium"
                  />
                )}
                <Chip
                  label={autoAcceptEnabled ? "ON" : "OFF"}
                  size="small"
                  color={autoAcceptEnabled ? "success" : "default"}
                />
              </Stack>
            </Stack>
          </Paper>

          <Divider />

          {/* Manual Accept */}
          <Paper
            elevation={3}
            sx={{ p: 3, borderRadius: 2, backgroundColor: colors.primary[400] }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Manual Accept Now
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Instantly accept all {pendingCount ?? 0} pending order(s) right
              now, regardless of the auto-accept toggle.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              disabled={accepting || pendingCount === 0}
              onClick={handleManualAccept}
              startIcon={
                accepting ? (
                  <AutorenewIcon
                    sx={{ animation: "spin 1s linear infinite" }}
                  />
                ) : (
                  <CheckCircleOutlineIcon />
                )
              }
              sx={{ minWidth: 200 }}
            >
              {accepting
                ? "Accepting..."
                : `Accept All Pending (${pendingCount ?? 0})`}
            </Button>
          </Paper>

          {/* Info box */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.greenAccent[700]}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>How it works:</strong> When "Automatic Acceptance" is ON,
              the server runs a scheduled task every minute that confirms all
              pending (status=0) orders by setting their status to Confirmed
              (status=1). You can also trigger this manually anytime using the
              button above.
            </Typography>
          </Paper>
        </Stack>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}

export default AutoOrderAcceptance;
